import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Stack, Typography } from '@mui/material';

import { TicketDetailResult, TicketSummaryResult } from 'stores/TicketStoreAdapter';

type Props = {
  detail: TicketDetailResult;
  /** 関連チケットへ遷移 */
  onOpenTicket: (ticketId: string) => void;
  /** 子チケット作成 */
  onCreateChild: () => void;
};

/**
 * 関連チケットタブ。
 * - 子の場合: 親チケット + 兄弟チケット
 * - 親の場合: 子チケット一覧 + 「子チケット作成」ボタン
 */
const RelatedTicketsTab: React.FC<Props> = ({ detail, onOpenTicket, onCreateChild }) => {
  const isChild = !!detail.parentTicketId;

  return (
    <Stack spacing={2}>
      {isChild && detail.parentTicket && (
        <Section title="親チケット">
          <TicketRow
            ticket={detail.parentTicket}
            onClick={() => onOpenTicket(detail.parentTicket!.ticketId)}
          />
        </Section>
      )}

      {isChild && (
        <Section title={`兄弟チケット（${detail.siblingTickets.length}）`}>
          {detail.siblingTickets.length === 0 ? (
            <EmptyText />
          ) : (
            detail.siblingTickets.map((t) => (
              <TicketRow key={t.ticketId} ticket={t} onClick={() => onOpenTicket(t.ticketId)} />
            ))
          )}
        </Section>
      )}

      {!isChild && (
        <Section
          title={`子チケット（${detail.childTickets.length}）`}
          action={
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={onCreateChild}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              子チケット作成
            </Button>
          }
        >
          {detail.childTickets.length === 0 ? (
            <EmptyText />
          ) : (
            detail.childTickets.map((t) => (
              <TicketRow key={t.ticketId} ticket={t} onClick={() => onOpenTicket(t.ticketId)} />
            ))
          )}
        </Section>
      )}
    </Stack>
  );
};

const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  action,
  children,
}) => (
  <Box>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
        {title}
      </Typography>
      {action}
    </Stack>
    <Stack spacing={0.75}>{children}</Stack>
  </Box>
);

const TicketRow: React.FC<{ ticket: TicketSummaryResult; onClick: () => void }> = ({
  ticket,
  onClick,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    onClick={onClick}
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      px: 1.5,
      py: 0.75,
      cursor: 'pointer',
      transition: 'all 0.15s',
      '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
    }}
  >
    <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
      {ticket.managementId}
    </Typography>
    <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
      {ticket.title}
    </Typography>
    <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
  </Stack>
);

const EmptyText: React.FC = () => (
  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
    関連チケットはありません
  </Typography>
);

export default RelatedTicketsTab;
